import type { QueryClient } from '@tanstack/react-query';
import { contentKeys, mediaApi, getApiBase, type Media, type LiveEvent } from '@wordpuppi/shared';
import { contentMediaReferences, effectsFor, effectsForEvent, mediaTabsFor } from '@/components/baxter/toolEffects';
import { useBaxterEffects } from './baxterEffects';

type Call = { name: string; args?: unknown; previousData?: unknown };

function cachedMedia(client: QueryClient, siteSlug: string): Media[] {
  return client.getQueriesData<{ data?: Media | Media[] }>({ queryKey: contentKeys.media.all(siteSlug) })
    .flatMap(([, value]) => value?.data ? Array.isArray(value.data) ? value.data : [value.data] : []);
}

/** Reuse the ordinary change bus. Data comes from successful upload/read responses. */
export async function notifyMediaChange(client: QueryClient, siteSlug: string, rows: Media[], action: 'added' | 'updated' | 'removed' = 'updated') {
  if (useBaxterEffects.getState().noticeSiteSlug !== siteSlug) return;
  const at = rows.length ? useBaxterEffects.getState().mark(['media'], undefined, {
    siteSlug, call: { name: 'media_change' },
    text: action === 'added' ? 'Media Added' : action === 'removed' ? 'Media Deleted' : 'Media Updated',
    mediaTabs: mediaTabsFor(rows),
  }) : null;
  // Reuse an upload hook's in-flight refetch instead of cancelling/repeating it.
  try { await client.invalidateQueries({ queryKey: contentKeys.media.all(siteSlug) }, { cancelRefetch: false }); }
  finally { if (at !== null) useBaxterEffects.getState().settle(at); }
}

export async function notifyMediaReferences(client: QueryClient, siteSlug: string, call: Call) {
  const content = contentMediaReferences(call, siteSlug);
  if (!content || useBaxterEffects.getState().noticeSiteSlug !== siteSlug) return;
  const references = (text: string) => {
    const escapedSlug = siteSlug.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const candidates = text.match(new RegExp(`(?:https?:\\/\\/[^/\\s"'<>\\\\]+)?\\/media\\/${escapedSlug}\\/[^\\s"'<>\\\\)]+`, 'g')) ?? [];
    const apiOrigin = new URL(getApiBase(), window.location.href).origin;
    const urls = new Set(candidates.filter((url) => {
      try { return new URL(url, window.location.href).origin === apiOrigin || new URL(url, window.location.href).origin === window.location.origin; }
      catch { return false; }
    }));
    // Durable external URLs already loaded in this site's cache are also associations.
    for (const row of cachedMedia(client, siteSlug)) {
      if (row.mime_type?.startsWith('image/') && row.url && text.includes(row.url)) urls.add(row.url);
    }
    return urls;
  };
  const args = call.args as { id?: unknown; content_type?: unknown } | null;
  const family = args?.content_type === 'pages' ? contentKeys.pages : contentKeys.posts;
  const previous = call.previousData ?? (typeof args?.id === 'string'
    ? client.getQueriesData<{ data?: Record<string, unknown> | Record<string, unknown>[] }>({ queryKey: family.all(siteSlug) })
      .flatMap(([, value]) => value?.data ? Array.isArray(value.data) ? value.data : [value.data] : [])
      .find((row) => row.id === args.id) : undefined);
  const fields = JSON.parse(content) as Record<string, unknown>;
  let urls = references(content);
  if (previous && typeof previous === 'object' && !Array.isArray(previous)) {
    const prior = previous as Record<string, unknown>;
    // A list-cache projection without the submitted fields is not an old-content baseline.
    if (call.previousData !== undefined || Object.keys(fields).every((key) => key in prior)) {
      const before = references(JSON.stringify(prior));
      const after = references(JSON.stringify({ ...prior, ...fields }));
      urls = new Set([...before, ...after].filter((url) => before.has(url) !== after.has(url)));
    }
  }
  if (!urls.size) return;
  const results = await Promise.allSettled([...urls].map((url) => mediaApi.getByUrl(siteSlug, url)));
  const rows = results.flatMap((result) => result.status === 'fulfilled' && result.value?.mime_type?.startsWith('image/') ? [result.value] : []);
  if (rows.length) await notifyMediaChange(client, siteSlug, rows);
}

/** Successful tool result -> normal content notice plus proven media associations. */
export async function applyToolEffects(client: QueryClient, siteSlug: string, call: Call, result: { ok?: boolean; media_ids?: unknown; screenshot_url?: unknown; event?: LiveEvent }) {
  const args = call.args as { site_slug?: unknown; slug?: unknown; id?: unknown; ids?: unknown } | null;
  const target = args?.site_slug ?? args?.slug;
  if (!result.ok || (typeof target === 'string' && target !== siteSlug) || useBaxterEffects.getState().noticeSiteSlug !== siteSlug) return;
  if (call.name === 'wpp_import_media') {
    if (!Array.isArray(result.media_ids)) {
      await client.invalidateQueries({ queryKey: contentKeys.media.all(siteSlug) }, { cancelRefetch: false });
      return;
    }
    const ids = [...new Set(result.media_ids.filter((id): id is string => typeof id === 'string' && !!id))];
    if (!ids.length) return; // A successful import envelope can contain only failures.
    const reads = await Promise.allSettled(ids.map((id) => mediaApi.get(siteSlug, id)));
    const rows = reads.flatMap((read) => read.status === 'fulfilled' && read.value.data ? [read.value.data] : []);
    await notifyMediaChange(client, siteSlug, rows, 'added');
    return;
  }
  if (call.name === 'wpp_render_url') {
    if (typeof result.screenshot_url !== 'string' || !result.screenshot_url) return;
    const row = await mediaApi.getByUrl(siteSlug, result.screenshot_url).catch(() => null);
    await notifyMediaChange(client, siteSlug, row ? [row] : [], 'added');
    return;
  }
  if (['wpp_resize_media', 'wpp_delete_media', 'wpp_delete_media_bulk'].includes(call.name)) {
    const ids = new Set(Array.isArray(args?.ids) ? args.ids : [args?.id]);
    const rows = cachedMedia(client, siteSlug).filter((row) => ids.has(row.id));
    if (call.name === 'wpp_resize_media' && typeof args?.id === 'string') {
      const read = await mediaApi.get(siteSlug, args.id).catch(() => null);
      if (read?.data) rows.splice(0, rows.length, read.data);
    }
    if (rows.length) {
      await notifyMediaChange(client, siteSlug, rows, call.name === 'wpp_resize_media' ? 'updated' : 'removed');
      return;
    }
  }
  const { keys, domains, id } = result.event ? effectsForEvent(result.event, siteSlug) : effectsFor(call, siteSlug);
  let noticeCall = call;
  if (call.name === 'wpp_update_content' && id && !call.previousData) {
    const family = (call.args as { content_type?: string })?.content_type === 'pages' ? contentKeys.pages : contentKeys.posts;
    const previousData = client.getQueriesData<{ data?: Record<string, unknown> | Record<string, unknown>[] }>({ queryKey: family.all(siteSlug) })
      .flatMap(([, value]) => value?.data ? Array.isArray(value.data) ? value.data : [value.data] : []).find((row) => row.id === id);
    noticeCall = { ...call, previousData };
  }
  const at = domains.length ? useBaxterEffects.getState().mark(domains, id, { siteSlug, call: noticeCall, ...(result.event ? { text: result.event.notice ?? `${result.event.domain}: ${result.event.action.replaceAll('_', ' ')}` } : {}) }) : null;
  const associations = notifyMediaReferences(client, siteSlug, call);
  await Promise.allSettled([
    ...keys.map((queryKey) => client.invalidateQueries(queryKey.length ? { queryKey } : undefined, { cancelRefetch: false })),
    associations,
  ]);
  if (at !== null) useBaxterEffects.getState().settle(at);
}
